import { StrictFetcher, formatPrice } from '@this/src/util';
/* eslint-disable max-lines */
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAITTheme, type AITTheme } from '@this/src/components/shared/ui/theme';
import type Hotel from '@this/domain/hotel/hotel';
import type User from '@this/domain/user/user';
import type HotelList from '@this/domain/hotel/hotel_list';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import WarningIcon from '@material-ui/icons/Warning';
import Tooltip from '@material-ui/core/Tooltip';
import CachedIcon from '@material-ui/icons/Cached';
import { Text } from '@this/src/components/shared/ui/data_displays/typography';
import HotelDetailModalPcTemplate from '@this/components/reserve_trip/select/hotel_detail_modal/hotel_detail_modal_pc_temlate';
import MarginType from '@this/domain/organization/margin_type2';
import HotelModel from '@this/domain/hotel/hotel';
import _ from 'lodash';
import type { HotelJson } from '@this/domain/select_repository';
import type HotelStayPlan from '@this/domain/hotel/hotel_stay_plan';
import HotelDetailModalSpTemplate from '@this/components/reserve_trip/select/hotel_detail_modal/hotel_detail_modal_sp_template';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { AvailableRoomsText } from '@this/components/shared/hotel/available_rooms_text';
import { Box } from '../shared/box_parts';
import type { HotelResponse } from './hotel_candidate_box';

export const HotelBox = ({ children, ...props }: React.ComponentProps<typeof Box>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelBoxStyles(theme), [theme]);

  return (
    <Box {...props} style={{ ...styles.hotelBox, ...props.style }}>
      {children}
    </Box>
  );
};

export const HotelImageArea = ({ hotel }: { hotel: Hotel }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelImageAreaStyles(theme), [theme]);

  return (
    <div
      className="hotel-box-parts__image-area"
      style={{ ...styles.imageArea, backgroundImage: `url(${hotel.image})` }}
    />
  );
};

export const HotelContentArea = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelContentAreaStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__content-area" style={styles.contentArea} {...props}>
      {children}
    </div>
  );
};

export const Left = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getLeftStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__left" style={styles.left} {...props}>
      {children}
    </div>
  );
};

const HotelNameArea = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelNameAreaStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__hotel-name-area" style={styles.nameArea}>
      {children}
    </div>
  );
};

export const HotelNameDiv = ({ children, inList }: { children: React.ReactNode; inList?: boolean | null }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelNameDivStyles(theme, inList), [theme, inList]);

  return (
    <div className="hotel-box-parts__hotel-name" style={styles.nameDiv} data-wovn-ignore>
      {children}
    </div>
  );
};

export const HotelName = ({
  hotel,
  inList,
  showManualBookingIcon
}: {
  hotel: Hotel;
  inList?: boolean;
  showManualBookingIcon?: boolean;
}) => (
  <HotelNameArea>
    <HotelNameDiv inList={inList} data-wovn-ignore>
      {hotel.name}
    </HotelNameDiv>
    {showManualBookingIcon && hotel.requires_manual_booking && (
      <Tooltip
        title="この候補はオペレーターによる手動予約が必要になる施設です。システムによる自動予約ではございません。"
        placement="right"
      >
        <span>
          <ManualBookingIcon />
        </span>
      </Tooltip>
    )}
  </HotelNameArea>
);

const ManualBookingIcon = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getManualBookingIconStyles(theme), [theme]);

  return <ErrorOutlineIcon style={styles.icon} className="hotel-box-parts__manual-booking-icon" />;
};

export const HotelBody = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getHotelBodyStyles(theme), [theme]);

  return (
    <div style={styles.body} {...props}>
      {children}
    </div>
  );
};

const Row = ({ children, margin, ...props }: React.HTMLAttributes<HTMLDivElement> & { margin?: string }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getRowStyles(theme, margin), [theme, margin]);

  return (
    <div style={styles.row} {...props}>
      {children}
    </div>
  );
};

const LastRow = ({ children, margin, ...props }: React.HTMLAttributes<HTMLDivElement> & { margin?: string }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getLastRowStyles(theme, margin), [theme, margin]);

  return (
    <div style={styles.lastRow} {...props}>
      {children}
    </div>
  );
};

const Cell = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getCellStyles(theme), [theme]);

  return (
    <div style={styles.cell} {...props}>
      {children}
    </div>
  );
};

const LabelCell = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getLabelCellStyles(theme), [theme]);

  return (
    <div style={styles.labelCell} {...props}>
      {children}
    </div>
  );
};

const PlanName = ({
  children,
  inList,
  inCell,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { inList?: boolean | null; inCell?: boolean | null }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getPlanNameStyles(theme, inList, inCell), [theme, inList, inCell]);

  return (
    <div style={styles.planName} {...props}>
      {children}
    </div>
  );
};

const RoomType = ({
  children,
  inList,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { inList?: boolean | null }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getRoomTypeStyles(theme, inList), [theme, inList]);

  return (
    <div style={styles.roomType} {...props}>
      {children}
    </div>
  );
};

interface Props {
  destLocation?: { lat: number; lng: number } | null;
  me?: User | null;
  inList?: boolean | null;
  user: User;
  hotel: Hotel;
  hotels: HotelList;
  hotelPriceLimit: number | null;
  searchQueryId: number | null;
  serviceId: number | undefined;
  handleChangeHotelStayPlan: (
    hotel: HotelJson,
    selectedPlan: HotelStayPlan,
    showFee: boolean,
    marginType: MarginType | undefined
  ) => void;
  submitButtonLabel?: string | null;
}

const HotelDetail = ({
  destLocation,
  me,
  inList,
  user,
  hotel,
  hotels,
  hotelPriceLimit,
  searchQueryId,
  serviceId,
  handleChangeHotelStayPlan,
  submitButtonLabel
}: Props) => {
  const { theme } = useAITTheme();
  const [selectedStayPlan, setSelectedStayPlan] = useState(
    hotel.stay_plans.find(plan => plan.id === hotel.selected_stay_plan_id)
  );

  useEffect(() => {
    setSelectedStayPlan(hotel.stay_plans.find(plan => plan.id === hotel.selected_stay_plan_id));
  }, [hotel.selected_stay_plan_id]);

  const enableMultipleHotelPlans = () => {
    if (hotel.isForeign()) {
      return me?.show_multiple_hotel_plans_foreign;
    }
    return me?.show_multiple_hotel_plans_domestic;
  };

  const detailButtonLabel = enableMultipleHotelPlans() ? '詳細・他プランを確認' : '詳細を確認';

  const [showDetailHotelPc, setShowDetailHotelPc] = useState<Hotel | null>(null);
  const [showHotelDetailModalPc, setShowHotelDetailModalPc] = useState<boolean>(false);
  const [showDetailHotelSp, setShowDetailHotelSp] = useState<Hotel | null>(null);
  const [showHotelDetailModalSp, setShowHotelDetailModalSp] = useState<boolean>(false);

  const showDetailHotelModalPc = (detailHotel: Hotel, hotels: HotelList) => {
    setShowHotelDetailModalPc(true);
    fetchAndShowDetailHotel(detailHotel, setShowDetailHotelPc, hotels);
  };

  const closeHotelDetailModalPc = () => {
    setShowHotelDetailModalPc(false);
    setShowDetailHotelPc(null);
  };

  const showDetailHotelModalSp = (detailHotel: Hotel, hotels: HotelList) => {
    setShowHotelDetailModalSp(true);
    fetchAndShowDetailHotel(detailHotel, setShowDetailHotelSp, hotels);
  };

  const closeHotelDetailModalSp = () => {
    setShowHotelDetailModalSp(false);
    setShowDetailHotelPc(null);
  };

  const fetchAndShowDetailHotel = (
    detailHotel: Hotel,
    setShowDetailHotel: (hotel: HotelModel) => void,
    hotels: HotelList
  ) => {
    StrictFetcher.get<HotelResponse>(`/hotels/${detailHotel.id}`, 'hotels/show.json').then(result => {
      const localHotel = hotels.find(result.hotel.id);
      let marginType: MarginType | undefined;
      if (result.margin_type) {
        marginType = new MarginType(result.margin_type);
      }
      // ホテルモデルの更新
      const uphotel = new HotelModel(
        _.merge(result.hotel, {
          plan_name: localHotel?.plan_name,
          selected_stay_plan_id: localHotel?.selected_stay_plan_id,
          showFee: result.show_fee,
          marginType,
          walkminute: detailHotel.walkminute
        })
      );
      setShowDetailHotel(uphotel);
    });
  };
  return (
    <Body>
      <Row>
        {hotel.breakfast && <Breakfast />}
        {typeof selectedStayPlan?.smoke === 'boolean' ? (
          <Smoke isSmoke={selectedStayPlan.smoke} />
        ) : typeof hotel.room_smoke === 'boolean' ? (
          <Smoke isSmoke={hotel.room_smoke} />
        ) : (
          ''
        )}
        {selectedStayPlan?.restricted && <RestrictedPlan />}
      </Row>
      <Row>
        <LabelCell className="select-hotel-box__detail-label">プラン：</LabelCell>
        <Cell
          data-wovn-ignore
          className="select-hotel-box__detail-value"
          style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          {hotel.planDisplayType() === 'tooltip' ? (
            <Tooltip title={hotel.plan_name ?? ''} placement="right">
              <PlanName inList={inList} inCell data-wovn-ignore>
                {hotel.plan_name}
              </PlanName>
            </Tooltip>
          ) : (
            <PlanName inList={inList} inCell data-wovn-ignore>
              {hotel.plan_name || '設定なし'}
            </PlanName>
          )}
        </Cell>
      </Row>
      {hotel.available_rooms != null && (
        <Row>
          <LabelCell className="select-hotel-box__detail-label">空室数：</LabelCell>
          <Cell className="select-hotel-box__detail-value">
            <AvailableRoomsText availableRooms={hotel.available_rooms} />
          </Cell>
        </Row>
      )}
      <Row>
        {hotel.plan_name !== hotel.room_type && (
          <Tooltip title={hotel.room_type ? hotel.room_type : ''} placement="right">
            <RoomType
              inList={inList}
              data-wovn-ignore
              className="select-hotel-box__detail-value"
              style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
            >
              {hotel.room_type}
            </RoomType>
          </Tooltip>
        )}
      </Row>
      <Row>
        <Cell className="select-hotel-box__detail-label">目的地まで：</Cell>
        <Cell data-wovn-ignore className="select-hotel-box__detail-value">
          {hotel.walkminute ? `徒歩${hotel.walkminute}` : hotel.latitude && hotel.longitude ? '(計算中...)' : '-'}
        </Cell>
      </Row>
      <Row>
        <Cell className="select-hotel-box__detail-label">最寄駅まで{hotel.nearestStationNameText()}：</Cell>
        <Cell data-wovn-ignore className="select-hotel-box__detail-value">
          {hotel.station_distance ? `徒歩${Math.floor(hotel.station_distance / 80)}分` : '-'}
        </Cell>
      </Row>
      <Row>
        <Cell className="select-hotel-box__detail-label">キャンセル：</Cell>
        <Cell className="select-hotel-box__detail-value">
          {hotel.package_type && theme.themeClass === 'mynavi' ? (
            <Link href="https://btm.mynavi.jp/terms/pdf/cancellation_policy_rakutendp.pdf" isExternal size="small">
              詳細を確認
            </Link>
          ) : hotel.package_type ? (
            `出発の21日前まで無料`
          ) : ['r', 'new_rakuten', 'agoda'].includes(hotel.type || '') && !hotel.sold_out ? (
            <>
              <HotelDetailModalPc>
                <Link onClick={() => showDetailHotelModalPc(hotel, hotels)} size="small">
                  詳細を確認
                </Link>
              </HotelDetailModalPc>
              <HotelDetailModalSp>
                <Link onClick={() => showDetailHotelModalSp(hotel, hotels)} size="small">
                  詳細を確認
                </Link>
              </HotelDetailModalSp>
            </>
          ) : theme.themeClass === 'tabikobo' && hotel.refund === '不可' ? (
            <WarningText>{hotel.refund}</WarningText>
          ) : (
            hotel.refund
          )}
        </Cell>
      </Row>
      {hotel.hasMultipleBedTypes() && (
        <Row>
          <Cell style={{ fontSize: '0.9em', margin: '4px 0', padding: '4px 0', color: '#dc3545' }}>
            {hotel.bedTypeNoticeMessage()}
          </Cell>
        </Row>
      )}
      {/*
      本当は新版APIのJRPackegeでもホテルの詳細リンクを表示したいが、表示手段が現状不明なためコメントアウトする。
      {(!(hotel.package_type || hotel.sold_out) || hotel.package_type === 'RP' || hotel.package_type === 'JR') && ( <Row>
          <Cell>
            <A href={hotel.detail_path || ''} target="_blank" rel="noreferrer noopener">
              詳細を見る
              {hotel.package_type === 'RP' || hotel.package_type === 'JR' ? '(外部サイトに移動します)' : null}
            </A>
          </Cell>
        </Row>
      )}
    */}
      {(!(hotel.package_type || hotel.sold_out || hotel.too_late) || hotel.package_type === 'RP') && (
        <LastRow margin="2px 0 0 0">
          <Cell>
            <HotelDetailModalPc>
              {hotel.package_type !== 'RP' && (
                <StyledDetailButton onClick={() => showDetailHotelModalPc(hotel, hotels)}>
                  {detailButtonLabel}
                </StyledDetailButton>
              )}
              {hotel.package_type === 'RP' && (
                <Link
                  href={hotel.detail_path || ''}
                  isExternal
                  target="_blank"
                  rel="noreferrer noopener"
                  size="small"
                  style={{
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  {detailButtonLabel}
                  {hotel.package_type === 'RP' ? '(外部サイトに移動します)' : null}
                </Link>
              )}
            </HotelDetailModalPc>
            <HotelDetailModalSp>
              {hotel.package_type !== 'RP' && (
                <StyledDetailButton onClick={() => showDetailHotelModalSp(hotel, hotels)}>
                  {detailButtonLabel}
                </StyledDetailButton>
              )}
              {hotel.package_type === 'RP' && (
                <Link
                  href={hotel.detail_path || ''}
                  isExternal
                  target="_blank"
                  rel="noreferrer noopener"
                  size="small"
                  style={{
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  {detailButtonLabel}
                  {hotel.package_type === 'RP' ? '(外部サイトに移動します)' : null}
                </Link>
              )}
            </HotelDetailModalSp>
          </Cell>
          <HotelDetailModalPcTemplate
            destLocation={destLocation}
            me={me}
            user={user}
            hotel={showDetailHotelPc}
            hotels={hotels}
            hotelPriceLimit={hotelPriceLimit}
            showModal={showHotelDetailModalPc}
            serviceId={serviceId}
            searchQueryId={searchQueryId}
            handleChangeHotelStayPlan={handleChangeHotelStayPlan}
            submitButtonLabel={submitButtonLabel}
            onClose={closeHotelDetailModalPc}
          />
          <HotelDetailModalSpTemplate
            destLocation={destLocation}
            me={me}
            user={user}
            hotel={showDetailHotelSp}
            hotels={hotels}
            hotelPriceLimit={hotelPriceLimit}
            showModal={showHotelDetailModalSp}
            serviceId={serviceId}
            searchQueryId={searchQueryId}
            handleChangeHotelStayPlan={handleChangeHotelStayPlan}
            submitButtonLabel={submitButtonLabel}
            onClose={closeHotelDetailModalSp}
          />
        </LastRow>
      )}
    </Body>
  );
};

const Body = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getBodyStyles(theme), [theme]);

  return (
    <div style={styles.body} {...props}>
      {children}
    </div>
  );
};

export const Right = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getRightStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__right" style={styles.right} {...props}>
      {children}
    </div>
  );
};

const Tag = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getTagStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__tag" style={styles.tag} {...props}>
      {children}
    </div>
  );
};

export const Breakfast = () => (
  <Tag>
    <span>朝食付き</span>
  </Tag>
);

const Smoke: React.FC<{ isSmoke: boolean }> = ({ isSmoke }) => {
  return (
    <Tag>
      <span>{isSmoke ? '喫煙' : '禁煙'}</span>
    </Tag>
  );
};

const RestrictedPlan: React.FC = () => {
  return (
    <RestrictedPlanTag>
      <span>
        <WarningIcon />
        制限付プラン
      </span>
    </RestrictedPlanTag>
  );
};

const RestrictedPlanTag = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getRestrictedPlanTagStyles(theme), [theme]);

  return (
    <div className="hotel-box-parts__restricted-plan-tag" style={styles.restrictedPlanTag} {...props}>
      {children}
    </div>
  );
};

const NoteDiv = ({ children, ...props }: React.ComponentProps<typeof Text>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getNoteDivStyles(theme), [theme]);

  return (
    <Text color="danger" level="caption" style={styles.noteDiv} {...props}>
      {children}
    </Text>
  );
};

export const Note = ({ hotel, hotelPriceLimit }: { hotel: Hotel; hotelPriceLimit: number | null }) => {
  const limit = hotelPriceLimit || 0;
  const isOverLimit = () => {
    return limit > 0 && hotel.getAveragePrice()! > limit;
  };

  if (!isOverLimit()) {
    return null;
  }

  return (
    <NoteDiv>
      <span>規程金額</span>
      <span data-wovn-ignore>{` +${formatPrice(hotel.getAveragePrice()! - limit)}`}</span>
    </NoteDiv>
  );
};

interface NoteForPriceProps {
  price: number;
  stayDays?: number | null;
  roomNum?: number | null;
  hotelPriceLimit: number | null;
}

export const NoteForPrice = ({ price, stayDays, roomNum, hotelPriceLimit }: NoteForPriceProps) => {
  const limit = (hotelPriceLimit || 0) * (stayDays || 1) * (roomNum || 1);
  const isOverLimit = () => {
    return limit > 0 && price > limit;
  };

  if (!isOverLimit()) {
    return null;
  }

  return (
    <NoteDiv>
      <span>規定金額</span>
      <span data-wovn-ignore>{` +${formatPrice(price - limit)}`}</span>
    </NoteDiv>
  );
};

export const Rating = ({ rating }: { rating: number }) => <RatingDiv rating={rating} />;

const RatingDiv = ({
  rating,
  ...props
}: { rating: number | undefined } & React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getRatingDivStyles(theme, rating), [theme, rating]);

  return <div className="hotel-box-parts__rating" style={styles.rating} data-rating={rating} {...props} />;
};

export const AnnounceOverLimit = ({ isOverLimit, loading }: { isOverLimit: boolean; loading: boolean }) => {
  if (!isOverLimit) return null;
  if (loading) return null;
  return (
    <AnnounceOverLimitDiv>
      <StyledAnnouncementIcon />
      <span>規定値オーバー</span>
    </AnnounceOverLimitDiv>
  );
};

const AnnounceOverLimitDiv = ({ children, ...props }: React.ComponentProps<typeof Text>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getAnnounceOverLimitDivStyles(theme), [theme]);

  return (
    <Text color="danger" level="caption" style={styles.announceOverLimit} {...props}>
      {children}
    </Text>
  );
};

const StyledAnnouncementIcon = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyledAnnouncementIconStyles(theme), [theme]);

  return <AnnouncementIcon style={styles.announcementIcon} />;
};

export const FetchNewPriceText = ({ isOverLimit, loading }: { isOverLimit: boolean; loading: boolean }) => {
  if (isOverLimit) return null;
  if (loading) return null;
  return (
    <FetchNewPriceTextDiv>
      <StyledCachedIcon />
      <span>最新の料金を取得しました</span>
    </FetchNewPriceTextDiv>
  );
};

const FetchNewPriceTextDiv = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getFetchNewPriceTextDivStyles(theme), [theme]);

  return (
    <div style={styles.fetchNewPriceText} {...props}>
      {children}
    </div>
  );
};

const StyledCachedIcon = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyledCachedIconStyles(theme), [theme]);

  return <CachedIcon style={styles.cachedIcon} />;
};

const WarningText = ({ children, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getWarningTextStyles(theme), [theme]);

  return (
    <span style={styles.warningText} {...props}>
      {children}
    </span>
  );
};

const HotelDetailModalPc = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="hotel-box-parts__detail-modal-pc" {...props}>
      {children}
    </div>
  );
};

const HotelDetailModalSp = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="hotel-box-parts__detail-modal-sp" {...props}>
      {children}
    </div>
  );
};

const StyledDetailButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 8px;
  margin-top: 8px;
  color: ${props => props.theme.linkColor};
  background: white;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  box-sizing: border-box;
  appearance: none;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.linkColor};
    outline-offset: 2px;
  }
`;

export default HotelDetail;

// ============================================================================
// getStyles functions - styled-componentsからの移行
// ============================================================================

const getHotelBoxStyles = (_theme: AITTheme) => ({
  hotelBox: {
    flexWrap: 'wrap' as const,
    width: '100%',
    background: 'white',
    position: 'relative' as const
  }
});

const getHotelImageAreaStyles = (_theme: AITTheme) => ({
  imageArea: {
    width: '100%',
    minHeight: '110px',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center'
  }
});

const getHotelContentAreaStyles = (_theme: AITTheme) => ({
  contentArea: {
    flexGrow: 9999,
    display: 'flex',
    flexDirection: 'column' as const
  }
});

const getLeftStyles = (_theme: AITTheme) => ({
  left: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden' as const,
    padding: '5px 5px 12px 10px'
  }
});

const getHotelNameAreaStyles = (_theme: AITTheme) => ({
  nameArea: {
    display: 'flex'
  }
});

const getHotelNameDivStyles = (_theme: AITTheme, _inList?: boolean | null) => ({
  nameDiv: {
    fontSize: '16px',
    fontWeight: 'bold' as const,
    marginTop: '5px'
  }
});

const getManualBookingIconStyles = (theme: AITTheme) => ({
  icon: {
    color: theme.tokens.colors.info.primary, // #48a4c8
    fontSize: '20px',
    marginLeft: '4px',
    cursor: 'help' as const
  }
});

const getHotelBodyStyles = (_theme: AITTheme) => ({
  body: {
    display: 'flex',
    alignItems: 'flex-end'
  }
});

const getRowStyles = (_theme: AITTheme, margin?: string) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    ...(margin && { margin })
  }
});

const getLastRowStyles = (_theme: AITTheme, margin?: string) => ({
  lastRow: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    ...(margin && { margin })
  }
});

const getCellStyles = (_theme: AITTheme) => ({
  cell: {}
});

const getLabelCellStyles = (_theme: AITTheme) => ({
  labelCell: {
    flexShrink: 0
  }
});

const getPlanNameStyles = (_theme: AITTheme, _inList?: boolean | null, _inCell?: boolean | null) => ({
  planName: {
    width: '100%',
    maxWidth: '400px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

const getRoomTypeStyles = (_theme: AITTheme, _inList?: boolean | null) => ({
  roomType: {
    width: '100%',
    maxWidth: '400px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

const getBodyStyles = (_theme: AITTheme) => ({
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    width: '100%',
    minHeight: '126px',
    minWidth: 0,
    fontSize: '14px'
  }
});

const getRightStyles = (_theme: AITTheme) => ({
  right: {
    flexShrink: 0,
    marginBottom: '10px',
    paddingInline: '10px',
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-end'
  }
});

const getTagStyles = (_theme: AITTheme) => ({
  tag: {
    margin: '4px 4px 5px 0',
    height: '20px',
    width: 'auto', // 文字数に合わせて自動調整
    padding: '0 8px', // 左右に余白を追加
    fontSize: '12px',
    color: 'white',
    backgroundColor: '#aaa',
    fontWeight: 'bold' as const,
    borderRadius: '2px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'nowrap' as const // 折り返し防止
  }
});

const getRestrictedPlanTagStyles = (_theme: AITTheme) => ({
  restrictedPlanTag: {
    margin: '4px 4px 5px 0',
    height: '14px',
    width: 'auto',
    backgroundColor: '#aaa',
    fontWeight: 'bold' as const,
    borderRadius: '2px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: '4px',
    paddingRight: '4px'
  }
});

const getNoteDivStyles = (_theme: AITTheme) => ({
  noteDiv: {
    margin: '0 0 8px 10px',
    fontSize: '14px'
  }
});

const getRatingDivStyles = (_theme: AITTheme, _rating: number | undefined) => ({
  rating: {
    position: 'relative' as const,
    display: 'inline',
    letterSpacing: '0'
  }
});

const getAnnounceOverLimitDivStyles = (_theme: AITTheme) => ({
  announceOverLimit: {
    display: 'flex',
    gap: '2px',
    alignItems: 'center'
  }
});

const getStyledAnnouncementIconStyles = (_theme: AITTheme) => ({
  announcementIcon: {
    fontSize: '13px'
  }
});

const getFetchNewPriceTextDivStyles = (theme: AITTheme) => ({
  fetchNewPriceText: {
    color: theme.successColor,
    fontSize: '12px'
  }
});

const getStyledCachedIconStyles = (_theme: AITTheme) => ({
  cachedIcon: {
    fontSize: '12px',
    verticalAlign: 'bottom'
  }
});

const getWarningTextStyles = (theme: AITTheme) => ({
  warningText: {
    fontWeight: 'bold' as const,
    color: theme.redColor
  }
});
